import { useEffect, useState } from "react";
import { usePdfBlob } from "./usePdfBlob.js";

interface PdfViewerSource {
  id: number;
  collectionId: number;
  filename: string;
  /** Shown as a badge next to the filename */
  roleTag?: string;
  /** Human-readable scope label already computed by the parent table */
  scopeLabel?: string;
}

function roleLabel(tag?: string): string {
  if (tag === "BOOK_BACK_QUESTIONS") return "Book-Back Questions";
  if (tag === "CHAPTER_GUIDE") return "Chapter Guide";
  return "Textbook";
}

function roleBadgeClass(tag?: string): string {
  if (tag === "BOOK_BACK_QUESTIONS") return "badge-bbq";
  if (tag === "CHAPTER_GUIDE") return "badge-guide";
  return "badge-textbook";
}

/**
 * Full-height modal that embeds the browser's native PDF viewer for a
 * knowledge-source file.
 *
 * The Blob URL and the reason it is needed live in usePdfBlob. The same URL
 * backs the Download anchor so the user gets the real filename via the
 * `download` attribute rather than a UUID.
 */
export function PdfViewerModal({
  source,
  onClose,
}: {
  source: PdfViewerSource;
  onClose: () => void;
}) {
  const { blobUrl, loadError } = usePdfBlob(source.collectionId, source.id);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (isFullScreen) {
          setIsFullScreen(false);
        } else {
          onClose();
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullScreen, onClose]);

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className={`modal-backdrop pdf-viewer-backdrop ${isFullScreen ? "viewer-fullscreen-backdrop" : ""}`}
      onClick={handleBackdropClick}
    >
      <div
        className={`modal-card pdf-viewer-card ${isFullScreen ? "viewer-fullscreen-card" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="pdf-viewer-header">
          <div className="pdf-viewer-title">
            <span className="ks-filename" title={source.filename}>
              {source.filename}
            </span>
            <span className={`badge mini ${roleBadgeClass(source.roleTag)} pdf-viewer-badge`}>
              {roleLabel(source.roleTag)}
            </span>
            {source.scopeLabel && (
              <span className="mini muted pdf-viewer-scope">{source.scopeLabel}</span>
            )}
          </div>

          <div className="pdf-viewer-actions">
            {/* Fullscreen toggle button */}
            <button
              type="button"
              className="btn mini"
              onClick={() => setIsFullScreen((prev) => !prev)}
              title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullScreen ? "⤡ Exit Fullscreen" : "⛶ Fullscreen"}
            </button>
            {/* Download uses the same blob URL — gives the file its real name. */}
            {blobUrl && (
              <a
                className="btn mini"
                href={blobUrl}
                download={source.filename}
                title="Download PDF to your computer"
              >
                ↓ Download
              </a>
            )}
            {/* Open in new tab: also uses the blob URL so auth is not needed. */}
            {blobUrl && (
              <a
                className="btn mini"
                href={blobUrl}
                target="_blank"
                rel="noreferrer"
                title="Open PDF in a new browser tab"
              >
                ↗ New Tab
              </a>
            )}
            <button className="btn mini" onClick={onClose} title="Close viewer">
              ✕ Close
            </button>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="pdf-viewer-frame-wrap">
          {loadError ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "var(--red)",
                padding: 24,
                textAlign: "center",
              }}
            >
              {loadError}
            </div>
          ) : !blobUrl ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "var(--muted)",
              }}
            >
              Loading PDF…
            </div>
          ) : (
            <iframe
              className="pdf-viewer-frame"
              src={blobUrl}
              title={`PDF viewer — ${source.filename}`}
            />
          )}
        </div>
      </div>
    </div>
  );
}
